import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CategoryDto, DraftDto, ImportJobDto, MenuSummaryDto, MenuVersionDto, RestaurantDto, TableDto, TranslationOverviewDto } from "@dishfolio/contracts";
import { api } from "./api.ts";
import { useOrg } from "./org.tsx";

export function useRestaurants() {
  const { orgId } = useOrg();
  return useQuery({ queryKey: ["restaurants", orgId], queryFn: () => api.get<{ restaurants: RestaurantDto[] }>("/restaurants").then((r) => r.restaurants), enabled: Boolean(orgId) });
}

export function useMenus() {
  const { orgId } = useOrg();
  return useQuery({ queryKey: ["menus", orgId], queryFn: () => api.get<{ menus: MenuSummaryDto[] }>("/menus").then((r) => r.menus), enabled: Boolean(orgId) });
}

export function useMenu(menuId: string) {
  return useQuery({ queryKey: ["menu", menuId], queryFn: () => api.get<{ menu: MenuSummaryDto; frozenReason: string | null }>(`/menus/${menuId}`) });
}

export function useContent(menuId: string) {
  return useQuery({ queryKey: ["content", menuId], queryFn: () => api.get<{ categories: CategoryDto[] }>(`/menus/${menuId}/content`).then((r) => r.categories) });
}

export function useDraft(menuId: string) {
  return useQuery({ queryKey: ["draft", menuId], queryFn: () => api.get<{ draft: DraftDto }>(`/menus/${menuId}/draft`).then((r) => r.draft), staleTime: Infinity, refetchOnWindowFocus: false });
}

export function useVersions(menuId: string) {
  return useQuery({ queryKey: ["versions", menuId], queryFn: () => api.get<{ versions: MenuVersionDto[] }>(`/menus/${menuId}/versions`).then((r) => r.versions) });
}

export function useTranslations(menuId: string) {
  return useQuery({ queryKey: ["translations", menuId], queryFn: () => api.get<TranslationOverviewDto>(`/menus/${menuId}/translations`) });
}

export function useTables(restaurantId: string | undefined) {
  return useQuery({ queryKey: ["tables", restaurantId], queryFn: () => api.get<{ tables: TableDto[] }>(`/restaurants/${restaurantId}/tables`).then((r) => r.tables), enabled: Boolean(restaurantId) });
}

export function useImport(jobId: string) {
  return useQuery({
    queryKey: ["import", jobId],
    queryFn: () => api.get<{ job: ImportJobDto }>(`/imports/${jobId}`).then((r) => r.job),
    refetchInterval: (q) => (q.state.data && ["queued", "running"].includes(q.state.data.status) ? 1500 : false),
  });
}

/** Invalidate everything that depends on menu content after an edit. */
export function useInvalidateMenu(menuId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ["content", menuId] }), qc.invalidateQueries({ queryKey: ["menu", menuId] }), qc.invalidateQueries({ queryKey: ["menus"] }), qc.invalidateQueries({ queryKey: ["translations", menuId] })]);
}

export { useMutation, useQueryClient };
